import type React from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Check, ExternalLink, Calendar as CalendarIcon, Clock, MapPin, UserRound, Star, Trash2, X, Pencil, RefreshCw } from "../../../components/icons";
import { getContrastTextColor } from "../../../lib/color";
import { formatTime, formatDate, parseBrazilDate } from "../../utils/date";
import { navigate } from "../../../lib/router";
import { useLanguage } from "../../../contexts/LanguageContext";
import { apptLabels } from "../labels";
import { cn } from "../../../lib/utils";

interface AppointmentLike {
  id: number | string;
  start_datetime: string;
  end_datetime?: string | null;
  duration_minutes: number;
  employee_id: number;
  employee_name?: string;
  client_name?: string | null;
  client_id?: number | null;
  client_uid?: string | null;
  notes?: string | null;
  done?: boolean;
  evolution?: string | null;
  specialty_name?: string | null;
  employee_color?: string | null;
  recurrence_group_id?: number | null;
  recurring_plan_id?: number | null;
  has_late_payment?: boolean;
  access_token?: string | null;
  insurance_provider_id?: number | null;
  insurance_provider_name?: string | null;
}

interface Props {
  appt: AppointmentLike;
  onEdit: (a: AppointmentLike) => void;
  onFillEvolution?: (a: AppointmentLike) => void;
  requireEvolution?: boolean;
  currentEmployeeId?: number;
  onMarkDone?: (a: AppointmentLike) => void;
  onUnmarkDone?: (a: AppointmentLike) => void;
  onDelete?: (a: AppointmentLike) => void;
  bgColor?: string | null; // override background color (e.g., schedule view)
  className?: string; // allow container height control (e.g., h-full)
  showStatusActions?: boolean;
  interactive?: boolean;
}

export function AppointmentCard({ appt, onEdit, onFillEvolution, requireEvolution = true, currentEmployeeId, onMarkDone, onUnmarkDone, onDelete, bgColor, className, showStatusActions = true, interactive = true }: Props) {
  const { lang } = useLanguage();
  const t = apptLabels[lang].card;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const isDone = Boolean(appt.done);
  const isRecurring = Boolean(appt.recurrence_group_id || appt.recurring_plan_id);

  // Floating time: no timezone usage
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onEdit(appt);
    }
  };

  const pendingEvolution = requireEvolution && isDone && !(appt.evolution && appt.evolution.trim());

  // calcula horário de término a partir da duração
  const endTimeStr = (() => {
    const end = appt.end_datetime || null;
    if (!end) return null;
    try {
      // Se o end_datetime cair em outro dia (possível drift de TZ), preferimos recalcular visualmente
      const startD = parseBrazilDate(appt.start_datetime);
      const endD = parseBrazilDate(end);
      const pad2 = (n:number)=>String(n).padStart(2,'0');
      const sDay = `${startD.getFullYear()}-${pad2(startD.getMonth()+1)}-${pad2(startD.getDate())}`;
      const eDay = `${endD.getFullYear()}-${pad2(endD.getMonth()+1)}-${pad2(endD.getDate())}`;
      if (typeof window !== 'undefined') {
        // Logs para entender discrepâncias de horário
        try {
          const sFmt = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
          const eFmt = sFmt;
          // eslint-disable-next-line no-console
          console.log('[CardTimeDebug]', {
            id: appt.id,
            start_raw: appt.start_datetime,
            end_raw: end,
            start_ptbr: sFmt(startD),
            end_ptbr: eFmt(endD),
            sDay,
            eDay,
          });
        } catch {}
      }
      // Se dia mudou OU diferença não bate com duration -> corrige visualmente
      const diffMin = Math.round((+endD - +startD) / 60000);
      if (sDay !== eDay || (Number.isFinite(diffMin) && appt.duration_minutes && Math.abs(diffMin - appt.duration_minutes) >= 5)) {
        // Fallback: usa horário do fim calculado como start + duration apenas para exibição
        const calc = new Date(startD);
        calc.setMinutes(calc.getMinutes() + Number(appt.duration_minutes || 0));
        if (typeof window !== 'undefined') {
          try {
            const sFmt = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
            // eslint-disable-next-line no-console
            console.log('[CardTimeDebug:fallback]', {
              id: appt.id,
              fallback_end_ptbr: sFmt(calc),
              diffMin,
            });
          } catch {}
        }
        // Constrói string MySQL em TZ Brasil para evitar duplo shift de fuso ao formatar
        const dateStr = `${calc.getFullYear()}-${pad2(calc.getMonth()+1)}-${pad2(calc.getDate())}`;
        const timeStr = `${pad2(calc.getHours())}:${pad2(calc.getMinutes())}:${pad2(calc.getSeconds())}`;
        return `${dateStr} ${timeStr}`;
      }
      return end;
    } catch {
      return end;
    }
  })();

  // Pré-calcula strings formatadas e loga o que será exibido
  const startTimeStr = formatTime(appt.start_datetime);
  const displayEndStr = endTimeStr ? formatTime(endTimeStr) : null;
  if (typeof window !== 'undefined') {
    try {
      // eslint-disable-next-line no-console
      console.log('[CardTimeDebug:display]', {
        id: appt.id,
        start_display: startTimeStr,
        end_display: displayEndStr,
      });
    } catch {}
  }

  return (
    <div
      className={`relative group select-none rounded-xl border ${bgColor ? '' : 'bg-white'} shadow-sm pt-3 pb-3 px-4 flex flex-col gap-2 min-w-0 ${className || ''}`}
      onClick={interactive && !isDone ? () => onEdit(appt) : undefined}
      role={interactive && !isDone ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      aria-disabled={!interactive}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
    >
      {/* Faixa lateral com a cor do funcionário */}
      <div
        className="absolute left-1.5 top-2 bottom-2 w-1 rounded-full"
        style={{ backgroundColor: appt.employee_color || "#9ca3af" }}
      />

      {/* Hover Actions */}
      {interactive && showStatusActions && (
        <div className={cn(
          "absolute right-1.5 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-20 transition-opacity duration-200",
          !isMobile && "opacity-0 group-hover:opacity-100"
        )}>
          {!isDone && (
            <Button
              size="micro"
              variant="outline"
              className="h-7 w-7 p-0 rounded-full shadow-md bg-white border-0"
              onClick={(e) => {
                e.stopPropagation();
                if (appt.access_token) {
                  window.open(`/remarcar/${appt.access_token}`, "_blank");
                } else {
                  onEdit(appt);
                }
              }}
              title={t.reschedule || "Remarcar"}
            >
              <CalendarIcon className="h-4 w-4 text-blue-600" />
            </Button>
          )}

          <Button
            size="micro"
            variant="destructive"
            className="h-7 w-7 p-0 rounded-full shadow-md bg-red-600 hover:bg-red-700"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(appt);
            }}
            title={apptLabels[lang].daySchedule.menu.delete}
          >
            <Trash2 className="h-4 w-4 text-white" />
          </Button>
        </div>
      )}

      {/* Header: Nome e Especialidade/Status */}
      <div className="flex flex-col gap-1">
        {appt.has_late_payment && !isDone && (!appt.insurance_provider_id || appt.insurance_provider_id === 1) && (
          <div className="mb-1">
            <Badge
              variant="destructive"
              className="inline-flex items-center gap-1 border-0 px-1.5 py-0 text-[8px] h-4 font-black uppercase tracking-wider animate-pulse bg-red-600 text-white"
            >
              {t.pendingPayment}
            </Badge>
          </div>
        )}

        {appt.insurance_provider_id && appt.insurance_provider_name && !appt.insurance_provider_name.includes('Particular') && (
          <div className="mb-1">
            <Badge className="inline-flex items-center gap-1 border-0 px-1.5 py-0 text-[8px] h-4 font-medium bg-blue-100 text-blue-700">
              {appt.insurance_provider_name}
            </Badge>
          </div>
        )}

        <div className="min-w-0">
          <div className="text-sm font-bold text-neutral-900 leading-tight flex items-center gap-1.5 overflow-hidden">
            <span 
              className={appt.client_uid ? "truncate cursor-pointer hover:text-blue-600 transition-colors" : "truncate"}
              onClick={appt.client_uid ? (e) => {
                e.stopPropagation();
                navigate(`/app/clients/${appt.client_uid}`);
              } : undefined}
            >
              {appt.client_name || t.defaultTitle}
            </span>
            {appt.client_uid && (
              <button
                className="shrink-0 p-0.5 rounded-md hover:bg-neutral-100 text-neutral-400 hover:text-blue-600 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/clients/${appt.client_uid}`);
                }}
                title={t.viewClient}
              >
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
            {isRecurring && (
              <RefreshCw className="h-3 w-3 text-neutral-400 shrink-0" />
            )}
          </div>
          
          <div className="flex items-center gap-1.5 mt-0.5 overflow-hidden">
            {appt.specialty_name && (
              <div className="text-[11px] text-neutral-500 truncate">
                {appt.specialty_name}
              </div>
            )}
            {isDone && (
              <Badge
                variant="outline"
                className="inline-flex items-center gap-1 border-0 px-1 py-0 text-[8px] h-3.5 font-bold shrink-0"
                style={{
                  backgroundColor: appt.employee_color || "#059669",
                  color: getContrastTextColor(appt.employee_color || "#059669"),
                }}
              >
                <Check className="h-2 w-2" />
                {t.done}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {appt.employee_name && (
        <div className="text-[10px] text-neutral-500 flex items-center gap-1 opacity-80">
          <UserRound className="h-3 w-3" /> {appt.employee_name}
        </div>
      )}

      <div className="mt-auto pt-1">
        {pendingEvolution && onFillEvolution ? (
          <div className="flex flex-col gap-1.5 w-full">
            <Button
              size="sm"
              variant="outline"
              className="w-full text-[10px] font-semibold h-8 flex items-center justify-center px-1 bg-white text-black border-neutral-200 hover:bg-neutral-50 shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                onUnmarkDone?.(appt);
              }}
            >
              <span>{t.unmarkDone}</span>
            </Button>
            <Button
              size="sm"
              variant="primary"
              className="w-full text-[10px] font-semibold h-8 flex items-center justify-center px-1 shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                onFillEvolution(appt);
              }}
            >
              <span>{t.fillEvolution}</span>
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant={isDone ? "outline" : "primary"}
            className={`w-full text-[10px] font-semibold h-8 flex items-center justify-center px-3 transition-all ${
              isDone ? "bg-neutral-100 border-neutral-200 text-neutral-600 hover:bg-neutral-200" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (isDone) onUnmarkDone?.(appt);
              else onMarkDone?.(appt);
            }}
          >
            {isDone ? (
              <span>{t.unmarkDone}</span>
            ) : (
              <span>{t.markDone}</span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
